import {
  Grid,
  IconButton,
  TextField,
  LinearProgress,
  Card,
  CardContent,
  Tooltip,
  Chip,
  Stack,
} from "@mui/material";
import Box from "@mui/material/Box";
import ResponsiveDataGrid from "../../../../../components/MobileList/ResponsiveDataGrid";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { MdOutlineClear, MdOutlineNumbers } from "react-icons/md";
import {
  UserProductCount,
  setSearchBody,
} from "../../../../../app/slices/userProductCountSlice";
import { SelectUser } from "../../../../../components/Selectors/SelectUser";
import { SelectProducts } from "../../../../../components/Selectors/SelectProducts";
import SerialsDialog from "../serials";

const defaultSearch = {
  user_id: null,
  product_id: null,
  serial_number: "",
};

const UserProductCountTable = () => {
  const dispatch = useDispatch();

  const _UserProductCounts = useSelector(
    (state) => state.userProductCount.userProductCounts
  );
  const _SearchBody = useSelector((state) => state.userProductCount.searchBody);
  const _Users = useSelector((state) => state.user.usersList.data);
  const _Products = useSelector((state) => state.product.products.data);

  const [search, setSearch] = useState(defaultSearch);
  const [serialsRow, setSerialsRow] = useState(null);

  // Only serialized products have serials to show, and the lookup needs both ids.
  const canShowSerials = (row) =>
    Boolean(row?.is_serialized && row?.product_id && row?.user_id);
  const [pageSize, setPageSize] = useState(15);
  const [currentPage, setCurrentPage] = useState(0);

  const columns = [
    {
      field: "index",
      headerName: "#",
      flex: 0.3,
    },
    {
      field: "user_name",
      headerName: "المستخدم",
      flex: 1,
    },
    {
      field: "product_name",
      headerName: "المنتج",
      flex: 1.5,
      renderCell: (params) => (
        <Stack direction="row" spacing={0.5} alignItems="center">
          <span>{params.row.product_name}</span>
          {canShowSerials(params.row) ? (
            <Tooltip title="الارقام التسلسلية">
              <IconButton
                size="small"
                color="primary"
                onClick={(e) => {
                  e.stopPropagation();
                  setSerialsRow(params.row);
                }}
              >
                <MdOutlineNumbers />
              </IconButton>
            </Tooltip>
          ) : null}
        </Stack>
      ),
    },
    {
      field: "count",
      headerName: "العدد",
      flex: 0.7,
    },
    {
      field: "is_serialized",
      headerName: "رقم تسلسلي",
      flex: 0.8,
      renderCell: (params) =>
        params.row.is_serialized ? (
          <Chip size="small" color="primary" label="نعم" />
        ) : (
          <Chip size="small" label="كلا" />
        ),
    },
    {
      field: "is_stockable",
      headerName: "مخزني",
      flex: 0.8,
      renderCell: (params) =>
        params.row.is_stockable ? (
          <Chip size="small" color="primary" label="نعم" />
        ) : (
          <Chip size="small" label="كلا" />
        ),
    },
  ];

  useEffect(() => {
    const timeout = setTimeout(() => {
      dispatch(UserProductCount.getAll(_SearchBody));
    }, 500);
    return () => clearTimeout(timeout);
  }, [_SearchBody, dispatch]);

  useEffect(() => {
    dispatch(
      setSearchBody({
        user_id: search.user_id ?? "",
        product_id: search.product_id ?? "",
        serial_number: search.serial_number,
        skip: currentPage * pageSize,
        take: pageSize,
      })
    );
  }, [currentPage, dispatch, pageSize, search]);

  return (
    <Card>
      <CardContent>
        <Grid container rowSpacing={1} columnSpacing={2}>
          <Grid item xs={12} md={4}>
            <SelectUser
              onChange={(e, newValue) => {
                setSearch({
                  ...search,
                  user_id: newValue ? newValue.id : null,
                });
              }}
              value={_Users.filter((x) => x.id === search.user_id)[0] ?? null}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <SelectProducts
              onChange={(e, newValue) => {
                setSearch({
                  ...search,
                  product_id: newValue ? newValue.id : null,
                });
              }}
              value={
                _Products.filter((x) => x.id === search.product_id)[0] ?? null
              }
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              size="small"
              value={search.serial_number}
              onChange={(e) =>
                setSearch({ ...search, serial_number: e.target.value })
              }
              label="الرقم التسلسلي"
              fullWidth
            />
          </Grid>
          <Grid item xs="auto" md={1}>
            <Tooltip title="الغاء الفلتر">
              <IconButton
                onClick={() => {
                  setSearch(defaultSearch);
                  setPageSize(15);
                  setCurrentPage(0);
                }}
              >
                <MdOutlineClear />
              </IconButton>
            </Tooltip>
          </Grid>
          <Grid item xs={12}>
            <Box sx={{ height: { xs: "auto", md: "63vh" }, width: "100%" }}>
              <ResponsiveDataGrid
                gridHeight={null}
                rows={
                  _UserProductCounts.data?.map((x, i) => ({
                    ...x,
                    index: i + 1 + pageSize * currentPage,
                  })) ?? []
                }
                columns={columns}
                pageSize={pageSize}
                page={currentPage}
                rowCount={_UserProductCounts.total}
                loading={_UserProductCounts.loading}
                rowHeight={50}
                pagination
                paginationMode="server"
                selectionModel={[]}
                onPageChange={(newPage) => setCurrentPage(newPage)}
                rowsPerPageOptions={[15, 25, 50, 100]}
                onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
                components={{ LoadingOverlay: LinearProgress }}
                onRowClick={(params) =>
                  canShowSerials(params.row) && setSerialsRow(params.row)
                }
                mobile={{
                  titleField: "user_name",
                  subtitleField: "product_name",
                  highlightField: "count",
                }}
              />
            </Box>
          </Grid>
        </Grid>
        <SerialsDialog row={serialsRow} onClose={() => setSerialsRow(null)} />
      </CardContent>
    </Card>
  );
};

export default UserProductCountTable;
